import { nazwijBazeChmury } from '@/lib/wdrozenie-api';
import { usePotwierdzenie } from '@/components/OknoPotwierdzenia';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Check, Cloud, Copy, KeyRound, Loader2, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { listAgents } from '@/lib/agents-api';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import {
  blokDoWklejenia,
  nazwaWezlaMastera,
  odwolajTokenAgenta,
  pobierzTokenyAgenta,
  wydajTokenAgenta,
  wygladaNaTokenAgenta,
  type WydanyToken,
} from '@/lib/token-chmury';
import { fetchPrzegladWysp } from '@/lib/wyspy-sprzatanie';

/**
 * TOKEN I NAZWA BAZY DLA CHMUROWEGO MASTERA — jedno miejsce na dwie wartości.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ POWSTAŁO Z POMYŁKI, KTÓRA BYŁA WBUDOWANA W PORTAL (PESA, 27.08.2026). Wdrożenie wymaga
 *  DWÓCH sekretów, a ekran pokazywał JEDEN: kod rejestracyjny, z przyciskiem „Kopiuj". Token
 *  agenta dla chmurowego sidecara nie miał w całym froncie ani jednego ekranu — wydawało się go
 *  wklejając JavaScript w konsolę przeglądarki. Do sekretu sidecara trafił więc kod (`sve_…`)
 *  zamiast tokenu (`sva_…`); oba mają 47 znaków i różnią się jedną literą prefiksu, a objawem
 *  był `401` co dwie sekundy bez śladu w tabeli tokenów. Diagnoza poszła w cudzy stack.
 *
 *  ⚠️ KARTY NIE UKRYWAMY, GDY NIE DA SIĘ JEJ UŻYĆ — pokazujemy ją z POWODEM. Ukrycie było właśnie
 *  przyczyną tej pomyłki: nikt nie szuka rzeczy, o której nie wie, że istnieje. Dlatego w trybie
 *  zwykłym karta stoi na ekranie i mówi, czego brakuje.
 *
 *  ⚠️ ROZBITE NA BRAMKĘ I WNĘTRZE, i to nie jest upiększenie. Karta ma trzy wyjścia „nic nie
 *  rysujemy" (nie ma wyspy, konto zawężone do klienta, wyspy nie ma w przeglądzie), a po nich
 *  jeszcze `useMutation`. W jednym komponencie byłyby to HOOKI WYWOŁYWANE WARUNKOWO — React
 *  wywraca się wtedy przy pierwszej zmianie warunku („rendered fewer hooks than expected"),
 *  czyli nie u nas, a u kogoś, kto przełączył wyspę.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function TokenChmurowegoMastera() {
  const { groupId } = useIslands();
  const { user } = useAuth();
  const przeglad = useQuery({ queryKey: ['przeglad-wysp'], queryFn: fetchPrzegladWysp });

  /** Konta zawężone do klienta nie mają tu wstępu — API pilnuje tego `SdiOnlyGuard`-em. */
  if (groupId === null || user?.clientId !== null || user.role !== 'ADMIN') return null;

  const wyspa = przeglad.data?.wyspy.find((w) => w.id === groupId);
  if (wyspa === undefined) return null;

  return <Karta groupId={groupId} wyspa={wyspa} />;
}

type Wyspa = {
  code: string;
  name: string;
  tryb: 'ZWYKLY' | 'SMART_SYNC';
  masterDatabase: string | null;
  masterConfirmed: boolean;
  /** `null`/brak = mastera jeszcze nie zasiano, więc nazwę bazy wolno WPISAĆ. */
  masterNodeId?: string | null;
};

function Karta({ groupId, wyspa }: { groupId: string; wyspa: Wyspa }) {
  const { t } = useI18n();
  const { zapytaj, oknoPytania } = usePotwierdzenie();
  const qc = useQueryClient();
  const toast = useToast();

  const [nodeId, setNodeId] = useState('');
  const [swiezy, setSwiezy] = useState<WydanyToken | null>(null);
  /**
   * NAZWA BAZY CHMUROWEJ DO WPISANIA.
   *
   * ⚠️ PUSTY STAN ZNACZY „nic nie tknięto", więc pokazujemy w polu to, co stoi na wyspie.
   * Podstawienie wartości do stanu przy pierwszym renderze rozjechałoby się z odświeżeniem
   * przeglądu wysp po zapisie.
   */
  const [nowaBaza, setNowaBaza] = useState('');

  const smart = wyspa.tryb === 'SMART_SYNC';

  const tokeny = useQuery({
    queryKey: ['agent-tokens', groupId],
    queryFn: () => pobierzTokenyAgenta(groupId),
    /** W trybie zwykłym karta nie wydaje tokenów, więc nie ma po co pytać o listę. */
    enabled: smart,
  });

  /**
   * WĘZEŁ CHMUROWEGO MASTERA, KTÓRY NA TEJ WYSPIE NAPRAWDĘ STOI.
   *
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  ⚠️⚠️ POPRAWKA BŁĘDU, KTÓRY SAM WPROWADZIŁEM (28.08.2026). Karta po przejściu na „jeden token"
   *  szukała go po nazwie WYLICZONEJ z kodu wyspy (`chmura-mssql-<kod>`) i na testówce napisała
   *  „ten węzeł nie ma jeszcze tokenu", choć token był wydany — bo węzeł nazywa się tam
   *  `chmura-mssql-dbw`, a z kodu wyspy wychodzi `chmura-mssql-vendi-test-dbw`. Na PESA obie nazwy
   *  przypadkiem się zgadzały, więc błąd nie miał jak wyjść.
   *
   *  ⚠️ TO SAMO PSUŁO WYDAWANIE, i to gorzej niż napis: przycisk z pustym polem wystawiłby token
   *  dla nazwy, pod którą NIE MA żadnego węzła — czyli nie odnowiłby istniejącego, tylko zrobił
   *  drugi, dla widma. Ta poprawka naprawia oba objawy jedną wartością.
   *
   *  ⚠️ ROZPOZNAJEMY PO BRAKU AUTOMATU, nie po nazwie. To ta sama definicja, którą rozstrzyga API
   *  (`czyToZasianieMastera`, `powodOdmowyWskazania`): master wyspy nie ma własnego automatu, bo
   *  jest wyłącznie bazą. Nazwa jest dowolnym napisem od człowieka i nie wolno na niej opierać
   *  tożsamości.
   *
   *  ⚠️ WYLICZONA NAZWA ZOSTAJE JAKO PODPOWIEDŹ DLA PIERWSZEGO WYDANIA — wtedy węzła jeszcze nie
   *  ma, więc nie ma czego znaleźć.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  const agenci = useQuery({ queryKey: ['agents'], queryFn: () => listAgents(), enabled: smart });
  const domyslny = nazwaWezlaMastera(agenci.data?.agents ?? [], groupId, wyspa.code);

  const wydaj = useMutation({
    mutationFn: () =>
      wydajTokenAgenta({
        groupId,
        nodeId: nodeId.trim() === '' ? domyslny : nodeId.trim(),
        label: t('tokchm.label').replace('{wyspa}', wyspa.name),
      }),
    onSuccess: (w) => {
      setSwiezy(w);
      void qc.invalidateQueries({ queryKey: ['agent-tokens', groupId] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : t('tokchm.bladWydania')),
  });

  const odwolaj = useMutation({
    mutationFn: (id: string) => odwolajTokenAgenta(id),
    onSuccess: () => {
      toast.success(t('tokchm.odwolany'));
      void qc.invalidateQueries({ queryKey: ['agent-tokens', groupId] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : t('tokchm.bladOdwolania')),
  });

  const kopiuj = (co: string, komunikat: string): void => {
    void navigator.clipboard.writeText(co).then(
      () => toast.success(komunikat),
      /** Schowek bywa zablokowany (brak HTTPS, polityka przeglądarki) — wartość zostaje na ekranie. */
      () => toast.error(t('tokchm.kopiaNieudana')),
    );
  };

  const baza = wyspa.masterDatabase;
  /** Master zasiany = nazwa opisuje istniejącą bazę, więc pole edycji tu nie ma prawa stać. */
  const zasiany = (wyspa.masterNodeId ?? null) !== null;
  const doZapisu = (nowaBaza === '' ? (baza ?? '') : nowaBaza).trim();
  /** Ten sam wzorzec, co w API i w agencie: nazwa wchodzi do treści `RESTORE DATABASE [...]`. */
  const nazwaBazyOk = /^[A-Za-z_][A-Za-z0-9_$-]{0,127}$/.test(doZapisu);

  const zapiszBaze = useMutation({
    mutationFn: (database: string) => nazwijBazeChmury(groupId, database),
    onSuccess: (r) => {
      toast.success(t('tokchm.bazaZapisana').replace('{baza}', r.masterDatabase));
      setNowaBaza('');
      /** ⚠️ Nazwa stoi też w krokach wdrożenia i w ustawieniach wyspy — odświeżamy oba źródła. */
      void qc.invalidateQueries({ queryKey: ['przeglad-wysp'] });
      void qc.invalidateQueries({ queryKey: ['wdrozenie'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const blok = swiezy === null ? null : blokDoWklejenia(swiezy.token, baza);
  /**
   * TOKEN CHMUROWEGO MASTERA — JEDEN, ten dla TEGO węzła.
   *
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  ⚠️⚠️ ZASTĄPIŁO LISTĘ WYDANYCH TOKENÓW, zgłoszone wprost (28.08.2026): „wydane tokeny w ogóle
   *  wypierdol, bo są tu jakieś niepotrzebne liczniki użycia, gdzie agent i tak cały czas chodzi
   *  i zaraz tu będą dziwne liczby (…) po prostu widać jeden i nowy nadpisuje stary (nie będzie
   *  2 chmurowych serwerów SQL nigdy)".
   *
   *  ⚠️ LISTA POKAZYWAŁA TEŻ TOKENY MASZYN, i to był jej główny grzech: filtr szedł po
   *  `kind === 'DATABASE'`, a w Smart Syncu KAŻDA maszyna jest węzłem bazy — więc na karcie
   *  „Chmurowy master" stały cztery wpisy, z których trzy nie miały z chmurą nic wspólnego.
   *  Token maszyny odwołuje się tam, gdzie się nim zarządza: przy węźle w „Pozostałych węzłach".
   *
   *  ⚠️ „NOWY NADPISUJE STARY" JEST TERAZ PRAWDĄ PO STRONIE API, nie obietnicą ekranu:
   *  `AgentTokenService.issue` odwołuje poprzednie tokeny tego węzła w jednej transakcji
   *  z utworzeniem nowego — tak samo, jak od dawna robi to rejestracja kodem.
   *
   *  ⚠️ FILTRUJEMY PO NAZWIE WĘZŁA Z POLA OBOK, a nie po rodzaju. Nazwa jest tożsamością tego
   *  węzła w portalu i to ona rozstrzyga, o którym tokenie mówi ta karta.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  const wezelMastera = nodeId.trim() === '' ? domyslny : nodeId.trim();
  /* ⚠️ `domyslny` to teraz nazwa ISTNIEJĄCEGO węzła (gdy istnieje), więc puste pole odnawia token
     temu, który stoi — a nie zakłada drugiego pod wyliczoną nazwą. Patrz komentarz wyżej. */
  /** Lista z API jedzie `createdAt desc`, więc pierwszy pasujący jest najnowszy. */
  const token = (tokeny.data ?? []).find((x) => x.nodeId === wezelMastera) ?? null;
  /**
   * ⚠️ „NIE WIEM" TO OSOBNY STAN, NIE „NIE MA". Dopóki lista tokenów się nie wczytała (albo
   * zapytanie padło), nie wolno wnioskować, że węzeł tokenu nie ma — patrz zabezpieczenie 3
   * przy przycisku. Ten warunek jest jedyną różnicą między pytaniem a cichym odcięciem agenta.
   */
  const stanTokenuZnany = smart && !tokeny.isPending && !tokeny.isError;
  const maWaznyToken = token !== null && token.revokedAt === null;

  return (
    <section className="space-y-4 rounded-lg border border-border bg-card p-4">
      {oknoPytania}
      <header className="space-y-1">
        {/*
          ⚠️ BEZ AKAPITU WSTĘPNEGO — zgłoszone wprost (28.08.2026): „wywal ten opis". Tłumaczył,
          czym są te dwie wartości, ale stoją przy nich własne podpisy pól, a karta pokazuje się
          wyłącznie na wyspie w Smart Syncu, czyli komuś, kto już wie, po co tu wszedł.
        */}
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Cloud className="size-4" />
          {t('tokchm.title')}
        </h2>
      </header>

      {/*
        ⚠️ POWÓD ZAMIAST UKRYCIA — patrz komentarz przy komponencie. Karta niewidoczna to karta,
        której nikt nie szuka; karta z powodem uczy, czego brakuje i w którym etapie to zrobić.
      */}
      {!smart ? (
        <p className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          <ShieldAlert className="mt-0.5 size-4 shrink-0" />
          {t('tokchm.tylkoSmart')}
        </p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium">{t('tokchm.nodeId')}</span>
              <span className="text-[11px] leading-snug text-muted-foreground">{t('tokchm.nodeIdWhat')}</span>
              <Input
                className="h-8 font-mono text-xs"
                value={nodeId}
                placeholder={domyslny}
                onChange={(e) => setNodeId(e.target.value)}
              />
            </label>

            {/*
              NAZWA BAZY CHMUROWEJ — druga z dwóch wartości i jedyne miejsce, w którym widać ją
              OBOK tokenu.

              ══════════════════════════════════════════════════════════════════════════════════
               ⚠️⚠️ DO WPISANIA, DOPÓKI MASTERA NIE ZASIANO — odwrócone 02.09.2026 na zgłoszenie:
               „pozwól od razu edytować nazwę bazy w chmurze, bo teraz podstawia się to, co było
               na pierwszym dodanym agencie, a baza w chmurze może mieć inną nazwę niż pozostałe.
               Tu każda baza będzie się nazywać inaczej i to musi działać".

               Stara wersja brała nazwę wprost z wyspy i nie dawała jej ruszyć — a `masterDatabase`
               ustawia PIERWSZA REJESTRACJA, czyli nazwa bazy jednej z maszyn (`donaldson_slave_1`).
               W Smart Syncu każda maszyna ma własną bazę, więc ta nazwa nie miała nic wspólnego
               z tym, jak nazwie się master w chmurze — a odtworzenie pod inną nazwą nie stawia
               plakietki „master wyspy".

               ⚠️ PO ZASIANIU POLE ZNIKA. Wtedy nazwa nie jest już zapowiedzią, a opisem bazy,
               w której master NAPRAWDĘ stoi; zmiana idzie przez „Zmień bazę wyspy" z przepisaniem
               kodu wyspy. API odmawia tak samo, więc pole i serwer mówią jedno.
              ══════════════════════════════════════════════════════════════════════════════════
            */}
            <div className="flex flex-col gap-1 text-xs">
              <span className="font-medium">{t('tokchm.baza')}</span>
              <span className="text-[11px] leading-snug text-muted-foreground">{t('tokchm.bazaWhat')}</span>
              {zasiany ? (
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <code className="rounded bg-muted px-2 py-1 font-mono text-xs">SPS_DB={baza}</code>
                  <Badge variant={wyspa.masterConfirmed ? 'success' : 'warning'}>
                    {wyspa.masterConfirmed ? t('tokchm.bazaPotwierdzona') : t('tokchm.bazaNiepotwierdzona')}
                  </Badge>
                  <span className="text-[11px] text-muted-foreground">
                    {t('tokchm.bazaZasiana').replace('{wezel}', wyspa.masterNodeId ?? '—')}
                  </span>
                </div>
              ) : (
                <div className="mt-1 flex flex-wrap items-end gap-2">
                  <Input
                    className="h-8 w-56 font-mono text-xs"
                    value={nowaBaza === '' ? (baza ?? '') : nowaBaza}
                    placeholder="nazwa bazy w chmurze"
                    onChange={(e) => setNowaBaza(e.target.value)}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={zapiszBaze.isPending || !nazwaBazyOk}
                    title={nazwaBazyOk ? undefined : t('tokchm.bazaWzorzec')}
                    onClick={() => zapiszBaze.mutate(doZapisu)}
                  >
                    {zapiszBaze.isPending ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
                    {t('tokchm.bazaZapisz')}
                  </Button>
                  {baza !== null && (
                    <Badge variant={wyspa.masterConfirmed ? 'success' : 'warning'}>
                      {wyspa.masterConfirmed ? t('tokchm.bazaPotwierdzona') : t('tokchm.bazaNiepotwierdzona')}
                    </Badge>
                  )}
                </div>
              )}
              {!zasiany && (
                <span className="text-[11px] leading-snug text-muted-foreground">{t('tokchm.bazaPodpis')}</span>
              )}
            </div>
          </div>

          {/*
            ⚠️⚠️ ZANIM TO KLIKNIE CZŁOWIEK, MUSI WIEDZIEĆ, ŻE ODCINA DZIAŁAJĄCEGO AGENTA.
            ══════════════════════════════════════════════════════════════════════════════════════
             Wydanie nowego tokenu ODWOŁUJE poprzedni token tego węzła (`AgentTokenService.issue`),
             więc chmurowy master przestaje zapisywać w tej sekundzie i wraca dopiero z nowym
             tokenem wklejonym do sekretu instancji — czyli po ruchu na platformie, nie u nas.

             ⚠️ TRZY ZABEZPIECZENIA, KAŻDE NA INNĄ POMYŁKĘ, i żadne nie zastępuje pozostałych:

              1. OSTRZEŻENIE WIDOCZNE PRZED KLIKNIĘCIEM. Modal pojawia się po ruchu ręki; napis
                 pod przyciskiem widać, zanim się po nią sięgnie.

              2. PYTANIE Z FAKTAMI (nazwa węzła, prefiks, stan). „Jesteś pewien?" bez treści
                 klika się odruchowo; zdanie z nazwą węzła daje szansę zauważyć, że to nie ten.

              3. PRZYCISK MARTWY, DOPÓKI STAN TOKENU NIEZNANY — i to jest poprawka DZIURY, którą
                 miała pierwsza wersja: pytanie zależało od `token !== null`, a `token` jest `null`
                 także wtedy, gdy lista tokenów się JESZCZE NIE WCZYTAŁA. Szybkie kliknięcie (albo
                 wolne łącze) omijało więc pytanie, a serwer i tak odwoływał stary token. Brak
                 wiedzy nie ma prawa znaczyć „nie ma o co pytać".
            ══════════════════════════════════════════════════════════════════════════════════════
          */}
          <Button
            size="sm"
            disabled={wydaj.isPending || !stanTokenuZnany}
            title={maWaznyToken ? t('tokchm.nadpisze') : !stanTokenuZnany ? t('tokchm.stanNieznany') : undefined}
            onClick={async () => {
              if (maWaznyToken) {
                const pytanie = t('tokchm.nadpiszePytanie')
                  .replace('{wezel}', wezelMastera)
                  .replace('{prefiks}', token.prefix)
                  .replace('{stan}', token.lastUsedAt === null ? t('tokchm.stanNieuzyty') : t('tokchm.stanUzywany'));
                /** ⚠️ Groźne: stary token przestaje działać w tej sekundzie, a tego się nie cofa. */
                if (!(await zapytaj({ tytul: t('tokchm.wydaj'), tresc: pytanie, etykietaPotwierdz: t('tokchm.wydaj'), ton: 'grozne' }))) {
                  return;
                }
              }
              wydaj.mutate();
            }}
          >
            {wydaj.isPending ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />}
            {t('tokchm.wydaj')}
          </Button>
          {maWaznyToken && (
            <p className="flex items-start gap-1.5 text-xs text-[var(--warning)]">
              <ShieldAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              {t('tokchm.nadpisze')}
            </p>
          )}
          {!stanTokenuZnany && <p className="text-xs text-muted-foreground">{t('tokchm.stanNieznany')}</p>}

          {swiezy !== null && (
            /**
             * ⚠️ TREŚĆ TOKENU ISTNIEJE TYLKO TERAZ — w bazie leży SHA-256. Ten sam wygląd co przy
             * kodzie rejestracyjnym obok, świadomie: dwa sekrety wydawane z jednego ekranu mają
             * zachowywać się identycznie, bo różnica w zachowaniu czyta się jako różnica znaczeń.
             */
            <div className="space-y-2 rounded-md border border-[var(--warning)] bg-[color-mix(in_oklab,var(--warning)_8%,transparent)] p-3">
              <p className="flex items-center gap-2 text-xs font-medium">
                <ShieldAlert className="size-4" />
                {t('tokchm.razWarning')}
              </p>

              {/*
                ⚠️ DOWÓD RODZAJU SEKRETU, NIE OZDOBA. Cała ta karta powstała z pomyłki „nie ta
                rodzina", więc ekran pokazuje wprost, że wydał `sva_` o właściwej długości.
              */}
              <p className="text-[11px] text-muted-foreground">
                {t('tokchm.dowod')
                  .replace('{prefiks}', swiezy.token.slice(0, 4))
                  .replace('{dlugosc}', String(swiezy.token.length))}
                {wygladaNaTokenAgenta(swiezy.token) ? ' ✓' : ` — ${t('tokchm.dowodZly')}`}
              </p>

              {blok === null ? (
                <>
                  <code className="block overflow-x-auto rounded bg-background p-2 font-mono text-xs">
                    {swiezy.token}
                  </code>
                  <p className="text-[11px] text-[var(--warning)]">{t('tokchm.blokBrakBazy')}</p>
                </>
              ) : (
                <>
                  <pre className="overflow-x-auto rounded bg-background p-2 font-mono text-xs">{blok}</pre>
                  <p className="text-[11px] text-muted-foreground">{t('tokchm.blokOpis')}</p>
                </>
              )}

              <div className="flex flex-wrap gap-2">
                {blok !== null && (
                  <Button size="sm" onClick={() => kopiuj(blok, t('tokchm.skopiowanoOba'))}>
                    <Copy className="size-4" />
                    {t('tokchm.kopiujOba')}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => kopiuj(swiezy.token, t('tokchm.skopiowanoToken'))}>
                  <Copy className="size-4" />
                  {t('tokchm.kopiujToken')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSwiezy(null)}>
                  {t('tokchm.ukryj')}
                </Button>
              </div>
            </div>
          )}

          {/* JEDEN TOKEN TEGO WĘZŁA — uzasadnienie przy `token` wyżej. */}
          <div className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('tokchm.listaTytul')}
            </h3>
            {tokeny.isPending ? (
              <p className="text-xs text-muted-foreground">
                <Loader2 className="mr-1 inline size-3 animate-spin" />
                {t('common.loading')}
              </p>
            ) : token === null ? (
              <p className="text-xs text-muted-foreground">{t('tokchm.listaPusto')}</p>
            ) : (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border p-2 text-xs">
                {/* Prefiks (12 znaków) NIE jest sekretem — po nim rozpoznaje się token w logach. */}
                <code className="font-mono">{token.prefix}…</code>
                <span className="font-medium">{token.nodeId}</span>
                {token.revokedAt !== null ? (
                  <Badge variant="neutral">{t('tokchm.stanOdwolany')}</Badge>
                ) : token.lastUsedAt === null ? (
                  <Badge variant="warning">{t('tokchm.stanNieuzyty')}</Badge>
                ) : (
                  <Badge variant="success">{t('tokchm.stanUzywany')}</Badge>
                )}
                {/*
                  ⚠️ BEZ LICZNIKA UŻYĆ. Agent puka co minutę, więc liczba rosła bez końca i nie
                  odpowiadała na żadne pytanie — zgłoszone jako „zaraz tu będą dziwne liczby".
                  Stan („używany") mówi to jedno, co z licznika było potrzebne.
                */}
                {/*
                  ⚠️ PYTANIE TAK SAMO JAK PRZY POZOSTAŁYCH ODWOŁANIACH — ujednolicone 28.08.2026
                  na żądanie („ujednolić to, niech wszystkie pytają tak samo").
                  ══════════════════════════════════════════════════════════════════════════════
                   Ten przycisk pytał INACZEJ niż token panelu i kod rejestracyjny: rozwijał
                   pytanie w wierszu, z dwoma małymi przyciskami. Wyglądało lepiej, ale trzy różne
                   sposoby pytania o to samo znaczą, że nawyk zdobyty na jednym ekranie nie działa
                   na drugim — a przy akcjach nieodwracalnych nawyk jest częścią zabezpieczenia.

                   ⚠️ ZAMIANA JEST TEŻ ZYSKIEM NA TREŚCI, nie tylko na spójności: tamto pytanie
                   brzmiało „odwołanie jest natychmiastowe i nieodwracalne" i NIE MÓWIŁO, o który
                   węzeł chodzi. Teraz podaje nazwę węzła, prefiks i stan — dokładnie jak dwa
                   pozostałe.
                  ══════════════════════════════════════════════════════════════════════════════
                */}
                {token.revokedAt === null && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-auto h-7"
                    disabled={odwolaj.isPending}
                    onClick={async () => {
                      const pytanie = t('tokchm.odwolajPytanie')
                        .replace('{wezel}', token.nodeId)
                        .replace('{prefiks}', token.prefix)
                        .replace('{stan}', token.lastUsedAt === null ? t('tokchm.stanNieuzyty') : t('tokchm.stanUzywany'));
                      if (!(await zapytaj({ tytul: t('tokchm.odwolaj'), tresc: pytanie, etykietaPotwierdz: t('tokchm.odwolaj'), ton: 'grozne' }))) {
                        return;
                      }
                      odwolaj.mutate(token.id);
                    }}
                  >
                    <Ban className="size-3.5" />
                    {t('tokchm.odwolaj')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
